import { $, must } from './dom.ts';
import type { AssignmentConfig, SiteMetadata, TestResults } from './evaluator-types.ts';
import { describe, fetchOk } from './fetch-helpers.ts';
import { type ControlMessage, JavaWsBackend } from './interactive-java.ts';
import type { DataFile, Message } from './terminal-engine.ts';
import { TerminalEngine } from './terminal-engine.ts';

/*
 * The bits of noVNC's RFB we use. noVNC is untyped and loaded at runtime
 * (see loadRFB), so this is our own faithful-but-minimal description.
 */
type RFBInstance = {
  resizeSession: boolean;
  scaleViewport: boolean;
  background: string;
  addEventListener(type: 'connect' | 'disconnect', listener: () => void): void;
};

type RFBConstructor = new (
  target: HTMLElement,
  url: string,
  options: { shared: boolean },
) => RFBInstance;

/*
 * Interactive Java GUI, backing the right-hand pane of a Swing assignment
 * (layouts/java-gui.njk; plans/java-gui-in-browser.md). The student's program
 * runs on their own per-user fly Machine against a real X display (Xvfb), so
 * plain javax.swing works unmodified. Two same-origin WebSockets through the
 * web app's authenticated machine proxy:
 *
 *   - /machine/ws/run (mode "gui") — the normal interactive-run protocol:
 *     we send the code, the machine compiles and runs it with DISPLAY set,
 *     and its stdout/stderr (and stdin!) stream through the "Output" tab's
 *     terminal exactly like a terminal assignment. The run ends when the
 *     program exits — for a Swing app, when its last window closes.
 *
 *   - /machine/ws/vnc — raw RFB, rendered by noVNC into the "Window" tab.
 *     The machine's x11vnc watches the Xvfb display the program draws on;
 *     mouse and keyboard flow back the same way.
 *
 * The first Run boots the machine (the proxy holds both handshakes while it
 * comes up), so it can take a few seconds; after that runs are quick.
 */

// noVNC ships as ES modules using top-level await, which can't live in the
// main es6 IIFE bundle, so (like the Pyodide workers) it's loaded at runtime
// from the copy build.ts puts in public/js/novnc/. The non-literal specifier
// keeps esbuild from trying to bundle it.
const RFB_URL = '/js/novnc/core/rfb.js';
let rfbClass: Promise<RFBConstructor> | null = null;
const loadRFB = (): Promise<RFBConstructor> => {
  rfbClass ??= import(RFB_URL).then((m) => m.default as RFBConstructor);
  return rfbClass;
};

// The run socket, plus auto-switching to the Output tab when something the
// student must read lands there: a compile error, a server refusal, or the
// end-of-run notice (by exit time the window is gone from the display, so
// the Output tab is the only place with any signal).
class GuiRunBackend extends JavaWsBackend {
  onShowOutput: () => void = () => {};

  override onControl(msg: ControlMessage) {
    if (msg?.type === 'compile-error' || msg?.type === 'error' || msg?.type === 'exit') {
      this.onShowOutput();
    }
    super.onControl(msg);
  }
}

class InteractiveJavaGui {
  config: AssignmentConfig;
  message: Message;
  programBackend: GuiRunBackend;
  program: TerminalEngine;

  rfb: RFBInstance | null = null; // the live RFB instance (loaded lazily; see loadRFB)
  vncUp: Promise<void> | null = null; // pending/settled once the current connection is up
  vncRetries = 0;

  stopButton: HTMLButtonElement | null;

  constructor(
    config: AssignmentConfig,
    message: Message,
    _username: string,
    _metadata: SiteMetadata,
  ) {
    this.config = config;
    this.message = message;

    // Everything goes through the same-origin machine proxy, so serverUrl is
    // always '' here (unlike the shared-runner pages).
    const backend = new GuiRunBackend('program', config, '', {
      mode: 'gui',
      wsPath: '/machine/ws/run',
      notice: 'Starting your machine… (a fresh one can take a few seconds to boot)',
    });
    backend.onShowOutput = () => this.showTab('output');
    this.programBackend = backend;
    this.program = new TerminalEngine('#gui-terminal', backend, message, null);

    this.stopButton = $('.terminal-tabs .stop-code') as HTMLButtonElement | null;
    if (this.stopButton) this.stopButton.onclick = () => this.program.stop();

    for (const tab of document.querySelectorAll<HTMLElement>('.terminal-tabs .tab[data-tab]')) {
      tab.onclick = () => this.showTab(must(tab.dataset.tab, 'data-tab on terminal tab'));
    }

    // Fullscreen the whole pane (tab bar included, so Stop and the Output tab
    // stay reachable). noVNC's resizeSession follows the pane's new size, so
    // the remote desktop grows to fill the screen; the output terminal needs
    // an explicit refit on both enter and exit.
    const fullscreen = $('.terminal-tabs .fullscreen-pane') as HTMLButtonElement | null;
    const pane = $('.terminal-pane') as HTMLElement | null;
    if (fullscreen && pane) {
      fullscreen.onclick = () => {
        if (document.fullscreenElement) {
          document.exitFullscreen();
        } else {
          pane.requestFullscreen().catch((err) => {
            console.error('Fullscreen request failed:', err);
          });
        }
      };
      document.addEventListener('fullscreenchange', () => {
        fullscreen.innerText = document.fullscreenElement ? 'Exit fullscreen' : 'Fullscreen';
        requestAnimationFrame(() => this.program.refit());
      });
    }
  }

  showTab(name: string) {
    for (const v of document.querySelectorAll('.terminal-view')) {
      v.classList.toggle('active', (v as HTMLElement).dataset.view === name);
    }
    for (const t of document.querySelectorAll('.terminal-tabs .tab')) {
      t.classList.toggle('active', (t as HTMLElement).dataset.tab === name);
    }
    if (name === 'output') {
      requestAnimationFrame(() => {
        this.program.refit();
        this.program.focus();
      });
    }
  }

  vncStatus(text: string) {
    const el = $('.vnc-status') as HTMLElement | null;
    if (el) {
      el.innerText = text;
      el.hidden = text === '';
    }
  }

  // Connect (or reconnect) the noVNC canvas. Idempotent while a connection
  // is live; returns a promise that settles once the connection is up (or
  // has failed — callers use it only for sequencing, never as a gate). A
  // dropped connection retries a few times — the machine may still be
  // booting on the very first Run — then waits for the next Run.
  connectVnc(): Promise<void> {
    this.vncUp ??= this.#openVnc();
    return this.vncUp;
  }

  async #openVnc(): Promise<void> {
    let RFB: RFBConstructor;
    try {
      RFB = await loadRFB();
    } catch (e) {
      this.vncUp = null;
      this.vncStatus('Could not load the display viewer.');
      console.error('noVNC load failed:', e);
      return;
    }
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const rfb = new RFB(
      $('#vnc-screen') as HTMLElement,
      `${proto}://${location.host}/machine/ws/vnc`,
      { shared: true },
    );
    // The machine's X server is TigerVNC's Xvnc, which honors SetDesktopSize:
    // resizeSession makes noVNC resize the remote framebuffer to match this
    // pane, so windows render 1:1 at native resolution. scaleViewport is the
    // fallback (scale-to-fit) for any server that can't resize.
    rfb.resizeSession = true;
    rfb.scaleViewport = true;
    rfb.background = '#1e1e1e';
    this.rfb = rfb;
    this.vncStatus('Connecting to your machine’s display…');

    await new Promise<void>((resolve) => {
      rfb.addEventListener('connect', () => {
        this.vncRetries = 0;
        this.vncStatus('');
        resolve();
      });
      rfb.addEventListener('disconnect', () => {
        resolve(); // never leave an execute() waiting on a dead connection
        if (this.rfb !== rfb) return;
        this.rfb = null;
        this.vncUp = null;
        if (this.vncRetries < 5) {
          this.vncRetries += 1;
          this.vncStatus('Display connection lost — reconnecting…');
          setTimeout(() => this.connectVnc(), 1500);
        } else {
          this.vncStatus('No display connection. Click ▶ Run to reconnect.');
        }
      });
    });
  }

  // A missing data file is a visible "Couldn't load <file> (404)" (the
  // run rejects; index.ts shows it), never a 404 page written into the
  // program's working directory as data.
  async #fetchDataFiles(): Promise<DataFile[]> {
    const files: string[] = this.config.dataFiles ?? [];
    return Promise.all(
      files.map(async (f) => {
        const res = await fetchOk(new URL(f, window.location.href)).catch((e) => {
          throw new Error(describe(e, `load ${f}`));
        });
        return {
          name: must(f.split('/').pop(), `file name in ${f}`),
          data: await res.arrayBuffer(),
        };
      }),
    );
  }

  async execute(
    code: string,
    _filename: string,
    others: { file: string; content: string }[] = [],
  ): Promise<TestResults> {
    this.programBackend.extraSources = others.map((o) => o.content);
    this.showTab('window');
    this.vncRetries = 0;
    // Get the display connection up BEFORE starting the program: connecting
    // is what resizes the desktop to match this pane (resizeSession), and a
    // window that centers itself on the desktop's pre-resize size can start
    // half off-screen. Time-boxed so a display problem never blocks a run,
    // with a beat after connect for the resize to land server-side.
    await Promise.race([
      this.connectVnc().then(() => new Promise((r) => setTimeout(r, 250))),
      new Promise((r) => setTimeout(r, 60_000)),
    ]);
    const dataFiles = await this.#fetchDataFiles();
    await this.program.run(code, dataFiles);
    return Object.create(null);
  }
}

const evaluator = (
  config: AssignmentConfig,
  message: Message,
  username: string,
  metadata: SiteMetadata,
) => new InteractiveJavaGui(config, message, username, metadata);

export default evaluator;
